// MegaMenuWrapper.tsx
'use client';

import React, { useEffect, useState, useRef } from 'react';
import '../../styles/enhanced-mega-menu.css';

// Type definitions
interface UrlSetting {
  type?: {
    id: string;
    name?: string;
  };
  collection?: {
    handle: string;
    title?: string;
  };
  blog?: {
    handle: string;
    title?: string;
  };
  page?: {
    handle: string;
    title?: string;
  };
  external_url?: string;
}

interface MenuItem {
  id: string;
  setting?: {
    title?: string;
    url?: UrlSetting | string;
    [key: string]: any;
  };
  menus?: MenuItem[];
}

// Declare global types for menu data
declare global {
  interface Window {
    React?: typeof React;
    megaMenuData?: any;
    customMenuData?: any;
    MegaMenu?: any;
  }
}

export const MegaMenuWrapper: React.FC = () => {
  const [menuData, setMenuData] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    // Prevent double mounting in development
    if (mountedRef.current) return;
    mountedRef.current = true;

    const loadMenuData = async () => {
      try {
        console.log('MegaMenuWrapper: Starting to load menu data...');
        
        // Make React available globally for compatibility
        if (typeof window !== 'undefined') {
          window.React = React;
        }

        // Try to load the menu data
        let data: MenuItem[] = [];
        
        // First try: Import the data module directly
        try {
          // Dynamic import with type assertion
          const dataModule = await import('../lib/enhanced-mega-menu-data.js');
          
          console.log('MegaMenuWrapper: Data module loaded:', dataModule);
          
          // Check if data is in window.customMenuData (set by the data file)
          if (typeof window !== 'undefined' && window.customMenuData?.data?.megamenu) {
            data = window.customMenuData.data.megamenu;
            console.log('MegaMenuWrapper: Data loaded from window.customMenuData:', data.length, 'items');
          }
        } catch (importError) {
          console.error('MegaMenuWrapper: Could not import data file:', importError);
          
          // Fallback: Check if data is already on window
          if (typeof window !== 'undefined') {
            if (window.customMenuData?.data?.megamenu) {
              data = window.customMenuData.data.megamenu;
              console.log('MegaMenuWrapper: Data found on window.customMenuData (fallback)');
            } else if (window.megaMenuData?.data?.megamenu) {
              data = window.megaMenuData.data.megamenu;
              console.log('MegaMenuWrapper: Data found on window.megaMenuData (fallback)');
            }
          }
        }

        if (data && data.length > 0) {
          setMenuData(data);
          setError(null);
          console.log('MegaMenuWrapper: Successfully loaded', data.length, 'menu items');
        } else {
          setError('No menu data available');
          console.warn('MegaMenuWrapper: No menu data found');
        }
      } catch (error) {
        console.error('MegaMenuWrapper: Error loading menu:', error);
        setError('Failed to load menu');
      } finally {
        setIsLoading(false);
      }
    };

    loadMenuData();
    
    return () => {
      // Cleanup
    };
  }, []);

  // Don't render anything while loading (prevents layout shift)
  if (isLoading) {
    return null;
  }

  // Don't render if there's an error or no data
  if (error || !menuData || menuData.length === 0) {
    console.log('MegaMenuWrapper: Not rendering due to:', error || 'No menu data');
    return null;
  }

  // Render the menu directly
  return <MegaMenuComponent menuData={menuData} />;
};

// The actual menu component
const MegaMenuComponent: React.FC<{ menuData: MenuItem[] }> = ({ menuData }) => {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedItems, setMobileExpandedItems] = useState(new Set<string>());
  
  const menuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  // URL generation
  const getItemUrl = (urlSetting?: UrlSetting | string): string => {
    if (!urlSetting) return '#';
    
    try {
      if (typeof urlSetting === 'string') return urlSetting;
      
      if (urlSetting.type) {
        const type = urlSetting.type;
        switch (type.id) {
          case 'collection':
            return urlSetting.collection ? `/collections/${urlSetting.collection.handle}` : '#';
          case 'home':
            return '/';
          case 'blog':
            return urlSetting.blog ? `/blogs/${urlSetting.blog.handle}` : '#';
          case 'page':
            return urlSetting.page ? `/pages/${urlSetting.page.handle}` : '#';
          case 'external':
            return urlSetting.external_url || '#';
          default:
            return '#';
        }
      }
      return '#';
    } catch (error) {
      console.warn('Error generating URL:', error);
      return '#';
    }
  };

  // Menu interactions
  const handleMenuEnter = (menuId: string) => {
    if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
    setActiveMenu(menuId);
    setIsMenuOpen(true);
    setActiveCategory(null);
  };

  const handleMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setIsMenuOpen(false);
      setActiveMenu(null);
      setActiveCategory(null);
    }, 200);
  };

  const handleCategoryHover = (categoryId: string) => {
    setActiveCategory(categoryId);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(prev => {
      const next = !prev;
      if (typeof document !== 'undefined') {
        document.body.style.overflow = next ? 'hidden' : '';
      }
      return next;
    });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
    setMobileExpandedItems(new Set());
  };

  const toggleMobileCategory = (categoryId: string) => {
    setMobileExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Responsive handling
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 1024);
        if (window.innerWidth >= 1024 && mobileMenuOpen) {
          closeMobileMenu();
        }
      }, 100);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [mobileMenuOpen]);

  // Listen for mobile toggle
  useEffect(() => {
    const handleMobileToggle = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target?.closest) return;
      
      if (target.closest('.filter-button') || target.closest('[data-action="open-filters"]')) {
        return;
      }
      
      if (target.closest('.toggle-menu') || target.closest('[data-action="toggle-browse"]')) {
        e.preventDefault();
        e.stopPropagation();
        toggleMobileMenu();
      }
    };
    
    document.addEventListener('click', handleMobileToggle, true);
    return () => document.removeEventListener('click', handleMobileToggle, true);
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, []);

  // Render dropdown panel
  const renderDropdownPanel = (menuItem: MenuItem) => {
    if (!menuItem.menus || menuItem.menus.length === 0) return null;

    const currentCategoryId = activeCategory || menuItem.menus[0]?.id;
    const selectedCategory = menuItem.menus.find(cat => cat.id === currentCategoryId);

    return (
      <div
        ref={dropdownRef}
        className="ca-dropdown-panel"
        onMouseEnter={() => {
          if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
          setIsMenuOpen(true);
        }}
        onMouseLeave={handleMenuLeave}
      >
        <div className="ca-dropdown-main">
          <h3 className="ca-dropdown-title">
            {menuItem.setting?.title || 'Categories'}
          </h3>
          {menuItem.menus.map(category => {
            const isActive = currentCategoryId === category.id;
            return (
              <div key={category.id} className={`ca-dropdown-category ${isActive ? 'ca-active' : ''}`}>
                <a
                  href={getItemUrl(category.setting?.url)}
                  className="ca-dropdown-category-link"
                  onMouseEnter={() => handleCategoryHover(category.id)}
                >
                  {category.setting?.title || 'Category'}
                </a>
              </div>
            );
          })}
        </div>
        {selectedCategory?.menus && selectedCategory.menus.length > 0 && (
          <div className="ca-dropdown-sub">
            <h3 className="ca-dropdown-sub-title">
              {selectedCategory.setting?.title || 'Items'}
            </h3>
            {selectedCategory.menus.map(subItem => (
              <a
                key={subItem.id}
                href={getItemUrl(subItem.setting?.url)}
                className="ca-dropdown-sub-link"
              >
                {subItem.setting?.title || 'Item'}
              </a>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render mobile menu item
  const renderMobileMenuItem = (item: MenuItem, level = 0): React.ReactNode => {
    const hasSubMenu = item.menus && item.menus.length > 0;
    const isExpanded = mobileExpandedItems.has(item.id);
    const title = item.setting?.title || 'Menu Item';
    const isCampsite = title.includes('Campsite') || title.includes('Find') || item.id === 'find-campsites';

    return (
      <li key={item.id} className={`mobile-menu-item level-${level} ${isCampsite ? 'campsite-item' : ''}`}>
        {hasSubMenu ? (
          <div className="mobile-menu-item-wrapper">
            <a href={getItemUrl(item.setting?.url)} className="mobile-menu-link">
              {title}
            </a>
            <button
              className={`mobile-expand-btn ${isExpanded ? 'expanded' : ''}`}
              onClick={() => toggleMobileCategory(item.id)}
              aria-label={`Expand ${title}`}
            >
              {isExpanded ? '−' : '+'}
            </button>
          </div>
        ) : (
          <a
            href={getItemUrl(item.setting?.url)}
            className={`mobile-menu-link ${isCampsite ? 'campsite-link' : ''}`}
            onClick={isCampsite ? undefined : closeMobileMenu}
          >
            {title}
          </a>
        )}
        {hasSubMenu && isExpanded && (
          <ul className="mobile-submenu">
            {item.menus!.map(subItem => renderMobileMenuItem(subItem, level + 1))}
          </ul>
        )}
      </li>
    );
  };

  // Mobile render
  if (isMobile) {
    return (
      <div className={`ca-mobile-menu-wrapper ${mobileMenuOpen ? 'menu-open' : ''}`}>
        {mobileMenuOpen && <div className="ca-mobile-overlay" onClick={closeMobileMenu} />}
        <div ref={mobileMenuRef} className={`ca-mobile-menu-panel ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="ca-mobile-menu-header">
            <h2>Menu</h2>
            <button className="ca-mobile-close-btn" onClick={closeMobileMenu} aria-label="Close menu">
              ×
            </button>
          </div>
          <div className="ca-mobile-menu-content">
            <ul className="ca-mobile-menu-list">
              {menuData.map(item => renderMobileMenuItem(item))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Desktop render
  return (
    <div className="ca-enhanced-menu">
      <div className="ca-menu-container">
        <nav className="ca-main-navigation">
          {menuData.map(item => {
            const hasDropdown = item.menus && item.menus.length > 0;
            const isActive = activeMenu === item.id && isMenuOpen;
            const title = item.setting?.title || 'Menu Item';
            const isCampsite = title.includes('Campsite') || title.includes('Find') || item.id === 'find-campsites' || item.id === 'campsite-menu';

            return (
              <div
                key={item.id}
                className={`ca-nav-item ${hasDropdown ? 'ca-has-dropdown' : ''} ${isActive ? 'ca-active' : ''} ${isCampsite ? 'ca-campsite' : ''}`}
                onMouseEnter={hasDropdown ? () => handleMenuEnter(item.id) : undefined}
                onMouseLeave={hasDropdown ? handleMenuLeave : undefined}
              >
                <a href={getItemUrl(item.setting?.url)} className="ca-nav-link">
                  {title}
                  {hasDropdown && (
                    <span className={`ca-dropdown-arrow ${isActive ? 'ca-active' : ''}`}>
                      ▼
                    </span>
                  )}
                </a>
                {hasDropdown && isActive && renderDropdownPanel(item)}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
};